<template>
  <div class="main__table">
    <v-data-table
      v-model="selected"
      :fixed-header="fixedHeader"
      :height="height"
      mobileBreakpoint="768"
      :headers="headers"
      :items="table"
      :item-key="itemKey"
      :group-by="groupBy"
      :expanded="expanded"
      class="elevation-1"
      hide-default-footer
      :items-per-page="table.length"
      :item-class="itemClass"
      @click:row="doClickRow"
      :disable-sort="disableSort"
      :single-select="singleSelect"
      :show-select="showSelect"
      :dense="dense"
    >
      <template v-for="slot in this.headerSlots" v-slot:[`header.${slot.split(headerSplitText)[0]}`]="{ header }">
        <slot :name="slot" :item="header"></slot>
      </template>
      <template v-for="slot in this.itemSlots" v-slot:[`item.${slot}`]="{ item }">
        <slot :name="slot" :item="item"></slot>
      </template>
      <template slot="no-data" v-if="this.noDataSlots">
        <slot :name="this.noDataSlots" class="px-8"></slot>
      </template>
      <template slot="body.append" v-if="this.bodyAppendSlots">
        <slot :name="this.bodyAppendSlots"></slot>
      </template>
      <template v-slot:[`group.header`]="{ items }" v-if="this.groupHeaderSlots">
        <slot name="group.header" :item="items"></slot>
      </template>
      <template v-slot:[`group.summary`]="{ items }" v-if="this.groupSummarySlots">
        <slot name="group.summary" :item="items"></slot>
      </template>
      <template v-slot:[`expanded-item`]="{ headers, item }" v-if="this.expandedItemSlots">
        <slot name="expanded-item" :item="item" :headers="headers"></slot>
      </template>
    </v-data-table>
    <div class="dataTable__scrollbar">
      <div class="dataTable__scrollbar__inner"></div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DefaultTable',
  props: {
    value: {
      type: [Array],
    },
    headers: {
      type: [Array],
    },
    table: {
      type: [Array],
    },
    itemClass: {
      type: [String, Function],
    },
    itemKey: {
      type: [String],
      default() {
        return 'name';
      },
    },
    fixedHeader: {
      type: [Boolean],
      default() {
        return false;
      },
    },
    dense: {
      type: [Boolean],
      default() {
        return false;
      },
    },
    height: {
      type: [String],
    },
    disableSort: {
      type: [Boolean],
      default() {
        return false;
      },
    },
    singleSelect: {
      type: [Boolean],
      default() {
        return false;
      },
    },
    showSelect: {
      type: [Boolean],
      default() {
        return false;
      },
    },
    groupBy: {
      type: [String],
    },
    expanded: {
      type: [Array],
    },
    targetId: {
      type: [String],
    },
  },
  data() {
    return {
      headerSplitText: '--header',
      barHeight: null,
      headerHeight: null,
      tableInnerWidth: null,
      windowWidth: null,
      selected: [],
      updateTime: 0,
      thresholdTime: 50,
    };
  },
  watch: {
    selected(val) {
      this.$emit('input', val);
    },
    value: {
      handler(val) {
        this.selected = val;
      },
      deep: true,
    },
  },
  beforeUnmount() {
    const target = this.targetId
      ? document.getElementById(this.targetId)
      : window;

    if (target) {
      target.removeEventListener('scroll', this.onScroll);
    }
  },
  mounted() {
  // 🔹 初始化表頭與視窗高度
    const barElement = document.querySelector('.v-toolbar.v-app-bar');
    const headerElement = this.$el.querySelector('.v-data-table-header');
    const bufferHeight = 2;

    this.barHeight = barElement ? barElement.clientHeight + bufferHeight : bufferHeight;
    this.headerHeight = headerElement ? headerElement.clientHeight : 0;
    this.windowHeight = window.innerHeight;

    // 🔹 初始化自訂 scrollbar 雙向同步
    const scrollbar1 = this.$el.querySelector('.elevation-1 .v-data-table__wrapper');
    const scrollbar2 = this.$el.querySelector('.dataTable__scrollbar');

    if (scrollbar1 && scrollbar2) {
      scrollbar1.onscroll = () => {
        scrollbar2.scrollLeft = scrollbar1.scrollLeft;
      };
      scrollbar2.onscroll = () => {
        scrollbar1.scrollLeft = scrollbar2.scrollLeft;
      };
    }

    // 🔹 等待 layout 穩定後再抓 table 寬度，避免為 0
    this.$nextTick(() => {
      this.waitForLayout(() => {
        const table = this.$el.querySelector('table');
        const innerScrollbar = this.$el.querySelector('.dataTable__scrollbar__inner');

        if (table && innerScrollbar) {
          this.tableInnerWidth = table.clientWidth;
          innerScrollbar.style.width = `${this.tableInnerWidth}px`;
        }

        this.handleScrollbar();
      });
    });

    // 🔹 監聽 Table 容器寬度變化
    if (scrollbar1) {
      const resizeObserver = new ResizeObserver(() => {
        this.waitForLayout(() => {
          const table = this.$el.querySelector('table');
          const innerScrollbar = this.$el.querySelector('.dataTable__scrollbar__inner');

          if (table && innerScrollbar) {
            this.tableInnerWidth = table.clientWidth;
            innerScrollbar.style.width = `${this.tableInnerWidth}px`;
          }

          this.handleScrollbar();
        });
      });
      resizeObserver.observe(scrollbar1);
    }

    // 🔹 加入滾動監聽事件
    const target = this.targetId
      ? document.getElementById(this.targetId)
      : window;

    if (target) {
      this.scrollTarget = target;
      target.addEventListener('scroll', this.onScroll);
    }
  },
  methods: {
    doClickRow(val) {
      this.$emit('doClickRow', val);
    },
    onScroll(e) {
      this.handleTableHeader(e);
      this.handleScrollbar(e);
    },
    handleTableHeader(e) {
      const el = this.$el;

      // 只查詢一次 tableHeader，減少不必要的 DOM 查詢
      const tableHeader = el.querySelector('.v-data-table-header');
      if (!tableHeader) return;

      // 確認滾動容器
      const scrollContainer = (e && e.currentTarget) || this.scrollTarget || window;
      const isInDialog = scrollContainer !== window;

      const barHeight = this.barHeight || 64; // 預設工具欄高度
      const headerHeight = this.headerHeight || 48; // 表頭高度

      // 用來固定表頭
      const addFixedHeader = (topValue) => {
        tableHeader.classList.add('headerFixed');
        tableHeader.style.transform = `translateY(${topValue}px)`;
        tableHeader.style.visibility = 'visible';
      };

      // 用來移除固定表頭
      const removeFixedHeader = () => {
        tableHeader.classList.remove('headerFixed');
        tableHeader.style.transform = '';
      };

      const tableRect = el.getBoundingClientRect();

      if (isInDialog) {
        // 滾動在 Dialog 中
        const containerRect = scrollContainer.getBoundingClientRect();
        const relativeTop = tableRect.top - containerRect.top;

        // 當表格表頭超出容器上邊界時固定
        if (relativeTop < 0) {
          addFixedHeader(Math.abs(relativeTop));
        } else {
          removeFixedHeader();
        }
      } else {
        // 滾動在普通頁面中
        const offsetTop = tableRect.top;
        const targetHeight = el.clientHeight - barHeight - headerHeight;

        const now = Date.now();
        const timeSinceLastUpdate = now - this.updateTime;
        const isThrottled = (expectedType) => timeSinceLastUpdate < this.thresholdTime && this.updateType === expectedType;

        // 判斷表格是否滾動超出一定範圍，並設定固定或移除表頭
        if (offsetTop + targetHeight < 0 || offsetTop >= barHeight) {
          if (isThrottled('addFixedHeader')) return;

          this.updateType = 'removeFixedHeader';
          this.updateTime = now;

          if (offsetTop !== 0 && offsetTop + targetHeight < 0) {
            tableHeader.style.visibility = 'hidden';
          } else {
            removeFixedHeader();
          }
        } else if (offsetTop < barHeight) {
          if (isThrottled('removeFixedHeader')) return;

          this.updateType = 'addFixedHeader';
          this.updateTime = now;

          addFixedHeader(-offsetTop + barHeight);
        }
      }
    },
    handleScrollbar(e) {
      const el = this.$el;

      // 只查詢一次 scrollbar DOM，提高效能
      const scrollbar = el.querySelector('.dataTable__scrollbar');
      if (!scrollbar) return;

      // 取得滾動容器
      const scrollContainer = (e && e.currentTarget) || this.scrollTarget || window;

      // 判斷是否在 Dialog 內（非 window 表示是內部容器）
      const isInDialog = scrollContainer !== window;

      // 防呆：避免 tableInnerWidth 尚未初始化
      if (!this.tableInnerWidth || this.tableInnerWidth <= 0) return;

      // 表格位置資訊
      const tableRect = el.getBoundingClientRect();

      // 滾動容器的可視範圍 bottom（視情況取 window 或容器自身）
      const containerBottom = isInDialog
        ? scrollContainer.getBoundingClientRect().bottom
        : window.innerHeight;

      // 表格底部相對於視窗或容器的位置
      const offsetBottom = tableRect.bottom;

      // 封裝隱藏 scrollbar 的邏輯
      const hideScrollbar = () => {
        scrollbar.classList.add('d-none'); // 隱藏 scrollbar
        scrollbar.classList.remove('headerFixed'); // 移除固定樣式
        scrollbar.style.transform = ''; // 清除定位值
      };

      // 封裝顯示 scrollbar 的邏輯
      const showScrollbar = (bottomValue) => {
        scrollbar.classList.remove('d-none'); // 顯示 scrollbar
        scrollbar.classList.add('headerFixed'); // 加上固定樣式
        scrollbar.style.transform = `translateY(-${bottomValue}px)`; // 動態設定定位位置
      };

      // ✅ 滾動判斷邏輯（與 handleTableHeader 保持一致）
      if (
        offsetBottom < containerBottom // 表格已在容器內
    || el.clientWidth >= this.tableInnerWidth // 沒有水平捲動
      ) {
        hideScrollbar();
      } else if (
        offsetBottom >= containerBottom // 表格超出容器底部
    || el.clientHeight > containerBottom // 或內容高度過大
      ) {
        showScrollbar(offsetBottom - containerBottom);
      }
    },
    waitForLayout(callback) {
    // 等待瀏覽器完成排版與繪製，避免抓不到寬度
      setTimeout(() => {
        requestAnimationFrame(callback);
      }, 200);
    },
  },
  computed: {
    headerSlots() {
      return Object.keys(this.$scopedSlots).filter((key) => key.includes('--header'));
    },
    itemSlots() {
      return Object.keys(this.$scopedSlots).filter((key) => !key.includes('--header'));
    },
    noDataSlots() {
      return Object.keys(this.$scopedSlots).filter((key) => key === 'no-data');
    },
    bodyAppendSlots() {
      return Object.keys(this.$scopedSlots).filter((key) => key === 'body.append');
    },
    groupHeaderSlots() {
      return Object.keys(this.$scopedSlots).filter((key) => key === 'group.header');
    },
    groupSummarySlots() {
      return Object.keys(this.$scopedSlots).filter((key) => key === 'group.summary');
    },
    expandedItemSlots() {
      return Object.keys(this.$scopedSlots).filter((key) => key === 'expanded-item');
    },
  },
};
</script>

<style lang="scss" scoped>

.main__table
{
  width: initial;
}

::v-deep .v-data-table--fixed-header thead th {
  background: #e6e6e6 !important;
}

::v-deep .headerFixed {
  position: relative !important;
  background-color: white !important;
  z-index: 1;
}

::v-deep .v-data-table-header th {
  text-align: center !important;
}

::v-deep .v-data-table-header th.sortable {
  // 隱藏排序箭頭icon
  i.mdi-arrow-up {
    display: none;
  }
}
// 排序時變更字體顏色 asc
::v-deep .v-data-table-header th.sortable.active.asc {
  color: red !important;
}
// 排序時變更字體顏色 desc
::v-deep .v-data-table-header th.sortable.active.desc {
  color: purple !important;
}

::v-deep .handler-group {
  display: flex;
  flex-direction: column;
  align-items: center;
  button {
    margin: 0.25rem;
    width: 160px;
  }

  &-start {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding-left: 1rem;
    span {
      text-align: start;
    }
  }

  &-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 160px;
    span {
      text-align: start;
    }
  }
}

::v-deep .v-row-group__summary {
  background: wheat !important;
}

/* 客製scrollbar */
.dataTable__scrollbar {
  width: 100%;
  overflow-x: scroll;
  overflow-y: hidden;

  &__inner {
    height: 1px;
    overflow: scroll;
  }
}
</style>
